import React, { useState, useEffect, useContext } from 'react'
import { gql, useMutation, useQuery } from '@apollo/client'
import { AuthContext } from '@/context/auth-context'
import { CORE_USER_FIELDS } from '@/graphql/user-fragments'
import { useForm } from '@/utils/form-hook'
import { usePhoneValidation } from '@/utils/phone-validation-hook'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import PhoneFormInput from '@/components/common/PhoneFormInput'
import { CubeLoader } from '@/components/ui/cube-loader'
import { cn } from '@/lib/utils'

const GET_USER = gql`
    ${CORE_USER_FIELDS}
    query GetUser($id: ID!) {
        users(where: { id: $id }) {
            ...CoreUserFields
        }
    }
`

const UPDATE_USER = gql`
    ${CORE_USER_FIELDS}
    mutation UpdateUser($id: ID!, $userInput: UserInput) {
        updateUser(id: $id, userInput: $userInput) {
            ...CoreUserFields
        }
    }
`

const FreeTrial = () => {
    const { user } = useContext(AuthContext)
    const [success, setSuccess] = useState(false)

    const { data, loading } = useQuery(GET_USER, {
        variables: { id: user.id },
        skip: !user?.id,
    })

    const userData = data?.users?.[0]
    const shouldShowForm = !userData?.firstName || !userData?.lastName || !userData?.businessName || !userData?.phone

    const initialValues = {
        firstName: userData?.firstName || '',
        lastName: userData?.lastName || '',
        businessName: userData?.businessName || '',
        phone: userData?.phone || '',
    }

    const updateUserCallback = async () => {
        await updateUser()
    }

    const { onChange, onSubmit, values, validated, setValue } = useForm(updateUserCallback, initialValues)

    const { isPhoneValid, phoneValidation } = usePhoneValidation(values)

    useEffect(() => {
        phoneValidation()
    }, [values.phone, phoneValidation])

    useEffect(() => {
        if (userData) {
            setValue({
                firstName: userData.firstName || '',
                lastName: userData.lastName || '',
                businessName: userData.businessName || '',
                phone: userData.phone || '',
            })
        }
    }, [userData, setValue])

    const [updateUser, mutation] = useMutation(UPDATE_USER, {
        onCompleted: () => {
            setSuccess(true)
        },
        variables: {
            id: user.id,
            userInput: {
                firstName: values.firstName,
                lastName: values.lastName,
                businessName: values.businessName,
                phone: values.phone,
                address: '',
                timeZoneOffset: 0,
                logoImage: null,
            },
        },
    })

    if (loading) {
        return <CubeLoader size="default" fullScreen={true} />
    }

    return (
        <div className="tw:flex tw:items-center tw:justify-center tw:min-h-[calc(100vh-200px)]">
            <div className="tw:w-full tw:max-w-2xl tw:px-4">
                <div className="tw:rounded-xs tw:bg-primary tw:border tw:shadow-sm tw:p-6">
                    <div className="tw:text-center tw:mb-8">
                        <h1 className="tw:text-3xl tw:font-semibold tw:mb-2">Welcome to Turba Media</h1>
                        <p className="tw:text-lg tw:mb-4">Your profile is now live.</p>
                        <p className="tw:text-sm tw:text-gray-100">
                            To help you get started quickly, we're offering free onboarding support and comprehensive
                            creative assistance. We'll help you build, launch, and optimise your campaigns for maximum
                            results.
                        </p>
                    </div>

                    {(success || !shouldShowForm) && (
                        <div className="tw:mb-6 tw:rounded-xs tw:border-2 tw:border-green-300 tw:p-4 tw:bg-green-700">
                            <div className="tw:flex tw:items-start tw:gap-4">
                                <div className="tw:flex-1">
                                    <span className="tw:leading-relaxed">
                                        Thank you. Your dedicated brand manager will contact you shortly to help you get started.
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {!success && shouldShowForm && (
                        <form noValidate>
                            <div className="tw:space-y-4">
                                <div className="tw:grid tw:grid-cols-2 tw:gap-4">
                                    <div className="tw:space-y-2">
                                        <Label htmlFor="firstName">First Name</Label>
                                        <Input
                                            required
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            placeholder="Enter First Name"
                                            value={values.firstName}
                                            onChange={onChange}
                                            className={cn(validated && !values.firstName && 'tw:border-destructive')}
                                        />
                                    </div>
                                    <div className="tw:space-y-2">
                                        <Label htmlFor="lastName">Last Name</Label>
                                        <Input
                                            required
                                            type="text"
                                            id="lastName"
                                            name="lastName"
                                            placeholder="Enter Last Name"
                                            value={values.lastName}
                                            onChange={onChange}
                                            className={cn(validated && !values.lastName && 'tw:border-destructive')}
                                        />
                                    </div>
                                </div>

                                <div className="tw:space-y-2">
                                    <Label htmlFor="businessName">Business Name</Label>
                                    <Input
                                        required
                                        type="text"
                                        id="businessName"
                                        name="businessName"
                                        placeholder="Enter Business Name"
                                        value={values.businessName}
                                        onChange={onChange}
                                        className={cn(validated && !values.businessName && 'tw:border-destructive')}
                                    />
                                </div>

                                <div className="tw:space-y-2">
                                    <Label htmlFor="phone">Phone</Label>
                                    <PhoneFormInput
                                        value={values.phone}
                                        setValue={setValue}
                                        validated={validated}
                                        isValid={isPhoneValid}
                                    />
                                    {validated && !isPhoneValid && (
                                        <p className="tw:text-sm tw:text-destructive tw:mt-1">
                                            Phone number is invalid
                                        </p>
                                    )}
                                </div>

                                <div className="tw:flex tw:justify-end">
                                    <Button
                                        type="button"
                                        onClick={onSubmit}
                                        disabled={mutation.loading || success}
                                        className=""
                                    >
                                        {mutation.loading ? 'Submitting...' : 'Start now'}
                                    </Button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    )
}

export default FreeTrial
